import React, { useEffect, useRef, useState } from 'react'
import * as styles from './SpacingField.module.scss'

export type Spacing = { top: string; right: string; bottom: string; left: string }

export const defaultSpacing: Spacing = { top: '0', right: '0', bottom: '0', left: '0' }

export const SpacingInput = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) => {
  // Local buffer so the caret survives the data round-trip: while the field
  // is focused, local state drives the input (typing preserves cursor);
  // external value changes only sync in when the field is NOT focused. Without
  // this, each keystroke echoes back through Puck's store and React resets the
  // caret to the end (breaks editing mid-string, e.g. calc()).
  const ref = useRef<HTMLInputElement>(null)
  const [local, setLocal] = useState(value)

  useEffect(() => {
    if (document.activeElement !== ref.current) setLocal(value)
  }, [value])

  return (
    <div className={styles.spacingInput}>
      <span className={styles.spacingInputLabel}>{label}</span>
      <input
        ref={ref}
        type="text"
        value={local}
        onChange={e => {
          setLocal(e.target.value)
          onChange(e.target.value)
        }}
        onBlur={() => setLocal(value)}
        placeholder="0px"
        className={styles.spacingInputField}
      />
    </div>
  )
}

export const SpacingGrid = ({
  title,
  value,
  onChange,
}: {
  title: string
  value: Spacing
  onChange: (v: Spacing) => void
}) => (
  <div className={styles.spacingGrid}>
    <span className={styles.spacingGridTitle}>{title}</span>
    <div className={styles.spacingGridInputs}>
      <SpacingInput
        label="Top"
        value={value?.top ?? '0'}
        onChange={(v) => onChange({ ...value, top: v })}
      />
      <SpacingInput
        label="Right"
        value={value?.right ?? '0'}
        onChange={(v) => onChange({ ...value, right: v })}
      />
      <SpacingInput
        label="Bottom"
        value={value?.bottom ?? '0'}
        onChange={(v) => onChange({ ...value, bottom: v })}
      />
      <SpacingInput
        label="Left"
        value={value?.left ?? '0'}
        onChange={(v) => onChange({ ...value, left: v })}
      />
    </div>
  </div>
)
